// File: components/navigation/BackToTop.js

import React from 'react';
import { scrollToTop } from '../../utils/helpers.js';
import { ICONS } from '../../utils/constants.js';

/**
 * BackToTop Component
 * 
 * Floating button that:
 * - Appears after scrolling down 300px
 * - Smooth scrolls to top when clicked
 * - Positioned bottom-right (adjusts if FAB present)
 * 
 * Mobile-optimized: 56px diameter, touch-friendly
 */
const BackToTop = ({ 
    scrollThreshold = 300,
    hasFAB = false,
    className = ''
}) => {
    const [isVisible, setIsVisible] = React.useState(false);

    // Monitor scroll position
    React.useEffect(() => {
        const handleScroll = () => {
            const scrolled = window.scrollY || document.documentElement.scrollTop;
            setIsVisible(scrolled > scrollThreshold);
        };

        // Throttle scroll events for performance
        let ticking = false;
        const throttledHandleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        // Add scroll listener
        window.addEventListener('scroll', throttledHandleScroll, { passive: true });

        // Check initial scroll position
        handleScroll();

        // Cleanup
        return () => {
            window.removeEventListener('scroll', throttledHandleScroll);
        };
    }, [scrollThreshold]);

    // Handle click - scroll to top
    const handleClick = () => {
        scrollToTop();
        
        // Optional: Focus on top element for accessibility
        const topElement = document.querySelector('.top-bar');
        if (topElement) {
            topElement.focus();
        }
    };

    return (
        <button
            className={`back-to-top ${isVisible ? 'visible' : ''} ${hasFAB ? 'with-fab' : ''} ${className}`}
            onClick={handleClick}
            aria-label="Back to top"
            aria-hidden={!isVisible}
            tabIndex={isVisible ? 0 : -1}
        >
            <span className="back-to-top-icon">{ICONS.ARROW_UP}</span>
        </button>
    );
};

export default BackToTop;
